<template>
  <div>
    <h5>Pagamentos Consolidados</h5>
    <v-alert type="info" dense text>
      <b>Entenda o relatório</b>
      <p class="mb-0">
        O relatório agrupa os pagamentos por forma de pagamento, data e evento.
      </p>
    </v-alert>
    <v-form v-model="valid" @submit.prevent>
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="startDate"
            label="Data de Início"
            type="datetime-local"
            outlined
            dense
            hint="Deixe em branco para pegar desde o início"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="endDate"
            label="Data de Fim"
            type="datetime-local"
            outlined
            dense
            hint="Deixe em branco para pegar até o momento"
          />
        </v-col>
        <v-col cols="12">
          <v-select
            label="Status"
            multiple
            v-model="status"
            :items="statuses"
            outlined
            dense
            :rules="[(v) => v.length > 0 || 'Selecione pelo menos um status']"
          />
        </v-col>
      </v-row>
    </v-form>
    <v-alert v-if="error" type="error">
      {{ error }}
    </v-alert>
    <template v-else-if="loading">
      <v-progress-linear
        :indeterminate="totalPayments <= processedPayments"
        :value="(processedPayments / totalPayments) * 100"
        color="primary"
        height="10"
        rounded
      />
      <p class="text-caption text-center mt-1">
        Os pagamentos estão sendo processados localmente, não feche a página.
      </p>
    </template>
    <v-alert v-else-if="succeeded" type="success">
      <b class="d-block">
        Processo finalizado com sucesso em {{ exportTime }} segundos
      </b>
      Foram processados {{ processedPayments | number }} pagamentos
    </v-alert>
    <div class="d-flex justify-space-between">
      <v-btn @click="$emit('close')" :disabled="loading" text>Fechar</v-btn>
      <v-btn @click="exportProcess" :loading="loading" color="primary">
        Exportar
      </v-btn>
    </div>
  </div>
</template>

<script>
import FINANCIAL from "@/services/admin/financial";
import moment from "moment";
import ExcelJS from "exceljs";
import creditCardType from "credit-card-type";

export const manifest = {
  name: "Pagamentos Consolidados",
  description: "Agrupa os pagamentos por forma de pagamento, data e evento.",
  icon: "mdi-arrow-collapse-all",
  group: "global",
  formats: ["xlsx"],
};

export default {
  name: "ConsolidatedPayments",
  data: () => ({
    loading: false,
    error: false,
    startDate: null,
    endDate: null,
    valid: false,
    succeeded: false,
    totalPayments: 0,
    processedPayments: 0,
    creditCardType: creditCardType,
    exportTime: 0,
    status: ["succeeded", "refunded"],
    statuses: [
      { text: "Aprovado", value: "succeeded" },
      { text: "Reembolsado", value: "refunded" },
      { text: "Cancelado", value: "canceled" },
      { text: "Pendente", value: "pending" },
    ],
  }),
  methods: {
    async exportProcess() {
      const startTime = new Date();
      try {
        this.loading = true;
        this.error = false;
        this.succeeded = false;
        this.totalPayments = 0;
        this.processedPayments = 0;

        let accumulator = {};
        let canContinue = true;
        let skip = 0;
        while (canContinue) {
          const { payments, total } = await FINANCIAL.getPayments({
            skip,
            take: 5000,
            status: this.status.join(","),
            startDate: moment(this.startDate).toISOString(),
            endDate: moment(this.endDate).toISOString(),
          });
          if (total) this.totalPayments = total;
          if (payments.length === 0) canContinue = false;

          payments.reduce(this.reducer, accumulator);
          skip += payments.length;
        }

        await this.exportExcel(accumulator);
        this.succeeded = true;
      } catch (error) {
        this.error = error.message || "Erro ao processar pagamentos";
        console.error(error);
      } finally {
        this.loading = false;
        this.exportTime = moment().diff(startTime, "seconds");
      }
    },
    reducer(acc, payment) {
      const { Ticket } = payment;
      const { TicketBlock } = Ticket[0];
      const { TicketGroup } = TicketBlock;
      const { Party } = TicketGroup;
      const { Organization } = Party;
      const { Platform } = Organization;
      const brand = this.defineBrand(payment);
      const paymentDate = moment(payment.createdAt).format("DD/MM/YYYY");

      const keyComponents = [
        Platform.name,
        Organization.name,
        Party.name,
        TicketGroup.name,
        TicketBlock.name,
        payment.paymentMethod,
        payment.paymentType,
        payment.installments,
        brand,
        paymentDate,
        payment.status,
      ];

      const key = keyComponents.join(":|:");

      if (!acc[key]) {
        acc[key] = {
          key,
          amount: 0,
          platformFee: 0,
          clientFee: 0,
          installmentsFee: 0,
          tickets: 0,
          payments: 0,
        };
      }

      acc[key].amount += payment.amount;
      acc[key].platformFee += payment.platformFee;
      acc[key].clientFee += payment.clientFee;
      acc[key].installmentsFee += payment.installmentsFee;
      acc[key].tickets += payment.Ticket.length;
      acc[key].payments++;
      this.processedPayments++;
      return acc;
    },
    async exportExcel(accumulator) {
      const workbook = new ExcelJS.Workbook();

      // Add metadata
      workbook.creator = "TiqOne";
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet("Pagamentos Consolidados");
      worksheet.columns = [
        { header: "Plataforma", key: "platform", width: 20 },
        { header: "Organização", key: "organization", width: 20 },
        { header: "Party", key: "party", width: 20 },
        { header: "Setor", key: "ticketGroup", width: 20 },
        { header: "Lote", key: "ticketBlock", width: 20 },
        { header: "Método de Pagamento", key: "paymentMethod", width: 20 },
        { header: "Tipo de Pagamento", key: "paymentType", width: 20 },
        { header: "Parcelas", key: "installments", width: 20 },
        { header: "Bandeira", key: "brand", width: 20 },
        { header: "Data", key: "paymentDate", width: 20 },
        { header: "Status", key: "status", width: 20 },
        { header: "Valor Carrinho", key: "amountCart", width: 20 },
        { header: "Valor Pago", key: "amount", width: 20 },
        {
          header: "Valor Repassado (produtor)",
          key: "producerAmount",
          width: 20,
        },
        { header: "Taxa da Plataforma", key: "platformFee", width: 20 },
        { header: "Taxa do Cliente", key: "clientFee", width: 20 },
        { header: "Taxa das Parcelas", key: "installmentsFee", width: 20 },
        { header: "Quantidade de Tickets", key: "tickets", width: 20 },
        { header: "Quantidade de Pagamentos", key: "payments", width: 20 },
      ];

      for (const key in accumulator) {
        const components = key.split(":|:");
        worksheet.addRow({
          platform: components[0],
          organization: components[1],
          party: components[2],
          ticketGroup: components[3],
          ticketBlock: components[4],
          paymentMethod: components[5],
          paymentType: components[6],
          installments: components[7],
          brand: components[8],
          paymentDate: moment(components[9], "DD/MM/YYYY").toDate(),
          status: components[10],
          amountCart: accumulator[key].amount - accumulator[key].clientFee,
          amount: accumulator[key].amount + accumulator[key].installmentsFee,
          producerAmount:
            accumulator[key].amount - accumulator[key].platformFee,
          platformFee: accumulator[key].platformFee,
          clientFee: accumulator[key].clientFee,
          installmentsFee: accumulator[key].installmentsFee,
          tickets: accumulator[key].tickets,
          payments: accumulator[key].payments,
        });
      }
      // format date as date
      worksheet.getColumn("paymentDate").style = { numFmt: "DD/MM/YYYY" };

      // format currency
      worksheet.getColumn("amount").numFmt = "R$ #,##0.00";
      worksheet.getColumn("amountCart").numFmt = "R$ #,##0.00";
      worksheet.getColumn("producerAmount").numFmt = "R$ #,##0.00";
      worksheet.getColumn("platformFee").numFmt = "R$ #,##0.00";
      worksheet.getColumn("clientFee").numFmt = "R$ #,##0.00";
      worksheet.getColumn("installmentsFee").numFmt = "R$ #,##0.00";

      worksheet.getRow(1).height = 20;
      worksheet.getRow(1).font = { name: "Arial", size: 12, bold: true };
      worksheet.getRow(1).alignment = {
        horizontal: "center",
        vertical: "center",
      };

      //   Save file
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Pagamentos Consolidados.xlsx";
        a.click();
      });
    },
    defineBrand(payment) {
      const binParsers = {
        POS: (extraData) => {
          return extraData?.bin;
        },
        CIELO: (extraData) => {
          const cardNumber = extraData?.payment?.CreditCard?.CardNumber;
          return cardNumber?.slice(0, 6);
        },
        PAGSEGURO: (extraData) => {
          const cardNumber = extraData?.payment_method?.card?.first_digits;
          return cardNumber;
        },
      };

      const extraData = payment.extraData;
      const binParser = binParsers[payment.paymentMethod];
      const bin = binParser ? binParser(extraData) : null;
      if (!bin) return null;
      const definer = this.creditCardType(bin);
      const brand = definer[0].niceType;

      return brand;
    },
  },
  watch: {
    loading: {
      handler(val) {
        this.$emit("loading", val);
      },
    },
  },
  beforeDestroy() {
    this.$emit("loading", false);
  },
};
</script>
